import { queryOptions } from '@tanstack/react-query'
import { ExpenseReportFilterShape, expenseReportsApi, shouldPollReport, ExpenseReport } from '.'
import { Pagination } from '@thinknimble/tn-models'

// Polling interval in milliseconds (3 seconds)
const POLL_INTERVAL = 3000

export const expenseReportQueries = {
  all: () => ['expense-reports'],

  retrieve: (id: string) =>
    queryOptions({
      queryKey: [...expenseReportQueries.all(), id],
      queryFn: () => expenseReportsApi.retrieve(id),
      enabled: Boolean(id),
    }),

  /**
   * Retrieve a report with automatic polling for active reports.
   * Polls every 3 seconds if the report is pending/in_progress and started within 30 minutes.
   * Shares the same cache key as `retrieve` so `setQueryData`/`invalidateQueries` calls
   * against `['expense-reports', id]` reach both subscribers (notably AsyncJobTracker's
   * invalidation on `export_report` completion).
   */
  retrieveWithPolling: (id: string) =>
    queryOptions({
      queryKey: [...expenseReportQueries.all(), id],
      queryFn: () => expenseReportsApi.retrieve(id),
      enabled: Boolean(id),
      refetchInterval: (query) => {
        const report = query.state.data as ExpenseReport | undefined
        if (report && shouldPollReport(report)) {
          return POLL_INTERVAL
        }
        return false
      },
    }),

  list: ({
    filters,
    pagination,
    paginationCallback,
  }: {
    filters?: Partial<ExpenseReportFilterShape>
    pagination?: Pagination
    paginationCallback?: (pagination: Pagination) => void
  }) =>
    queryOptions({
      queryKey: [...expenseReportQueries.all(), { filters, pagination }],
      queryFn: async () => {
        const res = await expenseReportsApi.list({ filters, pagination })
        const serverPagination = new Pagination({ ...res, totalCount: res.count })
        paginationCallback?.(serverPagination)
        return res
      },
      enabled: true,
    }),

  /**
   * List reports with automatic polling when any report is actively generating.
   * Polls every 3 seconds if any report in the list is pending/in_progress and started within 30 minutes.
   */
  listWithPolling: ({
    filters,
    pagination,
    paginationCallback,
  }: {
    filters?: Partial<ExpenseReportFilterShape>
    pagination?: Pagination
    paginationCallback?: (pagination: Pagination) => void
  }) =>
    queryOptions({
      queryKey: [...expenseReportQueries.all(), { filters, pagination }, 'polling'],
      queryFn: async () => {
        const res = await expenseReportsApi.list({ filters, pagination })
        const serverPagination = new Pagination({ ...res, totalCount: res.count })
        paginationCallback?.(serverPagination)
        return res
      },
      enabled: true,
      refetchInterval: (query) => {
        const data = query.state.data
        if (data?.results) {
          const hasActiveReport = data.results.some((report: ExpenseReport) =>
            shouldPollReport(report),
          )
          if (hasActiveReport) {
            return POLL_INTERVAL
          }
        }
        return false
      },
    }),

  generateReport: (id: string, templateId?: string | null) => {
    return queryOptions({
      queryKey: [...expenseReportQueries.all(), 'generate', id],
      queryFn: async () => await expenseReportsApi.csc.generateReport({ id, templateId }),
      enabled: Boolean(id),
    })
  },

  downloadReport: (id: string) =>
    queryOptions({
      queryKey: [...expenseReportQueries.all(), 'download', id],
      queryFn: async () => await expenseReportsApi.csc.downloadReport(id),
      enabled: Boolean(id),
    }),
}
